import { Link, useLocation } from "react-router-dom";
import { Compass, ArrowRight } from "lucide-react";

/**
 * Friendly 404 page. Routes users back to a sensible destination based on the
 * URL context they landed under:
 *   /care-portal/admin/*  → Admin Overview
 *   /care-portal/*        → Care Portal home
 *   everything else       → Public marketing home
 */
export default function NotFoundPage() {
  const { pathname } = useLocation();
  let destLabel = "Return home";
  let destTo = "/";
  let destTestId = "notfound-return-public-home";
  let context = "the CCCHAPS website";
  if (pathname.startsWith("/care-portal/admin")) {
    destLabel = "Return to Admin Overview";
    destTo = "/care-portal/admin/dashboard";
    destTestId = "notfound-return-admin";
    context = "the CCCHAPS admin center";
  } else if (pathname === "/care-portal" || pathname.startsWith("/care-portal/")) {
    destLabel = "Return to Care Portal";
    destTo = "/care-portal";
    destTestId = "notfound-return-care-portal";
    context = "the CCCHAPS Care Portal";
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16" data-testid="notfound-page">
      <div className="max-w-lg text-center">
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-6">
          <Compass className="w-6 h-6" strokeWidth={1.75} />
        </span>
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold" data-testid="notfound-eyebrow">Page not found</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
          We couldn&apos;t find that page.
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          The link you followed may be broken, or the page may have moved. You can return to {context} below.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <Link
            to={destTo}
            data-testid={destTestId}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors"
          >
            {destLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="mt-8 text-xs text-slate-400 truncate" data-testid="notfound-missing-path">
          Missing path: <span className="font-mono">{pathname}</span>
        </p>
      </div>
    </div>
  );
}
